(() => {
  if (window.__ugOktoberfestAiodPrices) return;
  window.__ugOktoberfestAiodPrices = true;

  const ROOT = '#ug-oktoberfest-page';
  const SECTION = '[data-ok-deals]';
  const CARD = '[data-ok-deals-card]';
  const REFRESH_MS = 1000;
  const initialCards = new WeakMap();

  function graphqlId(value) {
    const text = value == null ? '' : String(value);
    const parts = text.split('/');
    return (parts[parts.length - 1] || text).trim();
  }

  function parseExtra(extra) {
    if (!extra || extra.value == null) return null;
    if (typeof extra.value === 'object') return extra.value;
    try {
      return JSON.parse(extra.value);
    } catch (error) {
      return null;
    }
  }

  // Associate a rule with the campaign's buying collection, not its editable name.
  // AIOD remains responsible for evaluating cart quantities and checkout eligibility.
  function isRuleInScope(rule, collectionId) {
    if (!collectionId) return true;
    if (!Array.isArray(rule.condition)) return false;
    return rule.condition.some((group) => Array.isArray(group) && group.some((condition) => {
      if (!condition || condition.type?.value !== 'collection' || condition.operator?.value !== 'matchesAny') return false;
      return Array.isArray(condition.extra) && condition.extra.some((extra) => {
        const parsed = parseExtra(extra);
        return parsed && graphqlId(parsed.id) === collectionId;
      });
    }));
  }

  function isRuleActive(rule) {
    const dates = rule && rule.active_date;
    if (!dates) return true;
    const now = Date.now();
    const start = dates.start_date_time ? Date.parse(dates.start_date_time) : NaN;
    const end = dates.end_date_time ? Date.parse(dates.end_date_time) : NaN;
    if (Number.isFinite(start) && start > now) return false;
    if (Number.isFinite(end) && end < now) return false;
    if (Number.isFinite(start) && Number.isFinite(end) && start >= end) return false;
    return true;
  }

  function isTestRuleAllowed(rule) {
    const required = typeof rule.testModeEmail === 'string' ? rule.testModeEmail.trim().toLowerCase() : '';
    if (!required) return true;
    const session = window.aiodTestDiscountSession;
    const email = (session && typeof session.getEmail === 'function' && session.getEmail()) || window.aiod_test_discount_email || '';
    return String(email).trim().toLowerCase() === required;
  }

  function parseActionValue(raw) {
    if (raw == null || raw === '') return NaN;
    if (window.aiodResolveCurrencyValue) {
      const resolved = window.aiodResolveCurrencyValue(raw);
      const amount = parseFloat(resolved && resolved.value);
      if (Number.isFinite(amount)) return amount;
    }
    return parseFloat(String(raw).split(';')[0]);
  }

  function addCandidate(map, id, candidate) {
    if (!id) return;
    if (!map.has(id)) map.set(id, []);
    map.get(id).push(candidate);
  }

  function collectAiodPrices(section) {
    const byVariant = new Map();
    const byProduct = new Map();
    if (section.getAttribute('data-ok-aiod-enabled') === 'false') return { byVariant, byProduct };
    const preview = section.hasAttribute('data-ok-price-preview');
    const collectionId = section.getAttribute('data-ok-aiod-collection-id') || '';
    const rules = Array.isArray(window.aiod_automatic_discounts) ? window.aiod_automatic_discounts : [];

    rules.forEach((rule) => {
      if (!rule || (!preview && (!isRuleActive(rule) || !isTestRuleAllowed(rule)))) return;
      if (rule.discountMethod && rule.discountMethod !== 'automatic') return;
      if (!isRuleInScope(rule, collectionId)) return;

      (Array.isArray(rule.action) ? rule.action : []).forEach((action) => {
        if (!action || action.discountType !== 'final_price') return;
        if (action.type !== 'specific_variant' && action.type !== 'specific_product') return;
        if (action.operator !== 'inArray') return;
        const amount = parseActionValue(action.discountValue);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const candidate = { ruleId: String(rule.rule_id || ''), cents: Math.round(amount * 100) };
        (Array.isArray(action.extra) ? action.extra : []).forEach((extra) => {
          const parsed = parseExtra(extra);
          if (!parsed || parsed.isUnavailable) return;
          const id = graphqlId(parsed && parsed.id);
          if (action.type === 'specific_variant') addCandidate(byVariant, id, candidate);
          if (action.type === 'specific_product') addCandidate(byProduct, id, candidate);
        });
      });
    });

    return { byVariant, byProduct };
  }

  function resolveCardPrice(card, tables) {
    const ruleId = (card.getAttribute('data-ok-aiod-rule-id') || '').trim();
    const variantId = card.getAttribute('data-variant-id') || '';
    const productId = card.getAttribute('data-product-id') || '';
    const matchesRule = (candidate) => !ruleId || candidate.ruleId === ruleId;
    const variants = (tables.byVariant.get(variantId) || []).filter(matchesRule);
    const candidates = variants.length ? variants : (tables.byProduct.get(productId) || []).filter(matchesRule);
    const prices = [...new Set(candidates.map((candidate) => candidate.cents))];
    if (prices.length > 1) {
      return { message: '此商品匹配到多个不同的插件价格，请在商品区块填写 AIOD 规则 ID 指定活动规则。' };
    }
    if (!prices.length) {
      return { message: ruleId
        ? '指定的 AIOD 规则未匹配，请核对规则 ID、商品变体与活动商品集合。'
        : '暂未匹配插件价格，请核对 AIOD 的优惠商品变体、活动商品集合，并确认规则已下发。' };
    }
    const regularCents = Number(card.getAttribute('data-ok-regular-price-cents'));
    if (prices[0] <= 0 || prices[0] >= regularCents) {
      return { message: '插件价格未低于当前商品售价，已保留原价格。' };
    }
    return { amount: prices[0] / 100, message: '' };
  }

  function formatMoney(amount) {
    const currency = window.aiod_shop_currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    const locale = window.aiod_locale || (document.documentElement && document.documentElement.lang) || 'de-DE';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function applyCard(card, tables) {
    const result = resolveCardPrice(card, tables);
    const extraPrice = result.amount;
    const status = card.querySelector('[data-ok-aiod-status]');
    if (status) {
      const message = card.closest(SECTION)?.getAttribute('data-ok-aiod-enabled') === 'false' ? '' : result.message;
      if (status.textContent !== message) status.textContent = message;
      status.hidden = !message;
    }
    const label = card.querySelector('.ug-oktoberfest-deals__price-label');
    const value = card.querySelector('.ug-oktoberfest-deals__price-value');
    const compare = card.querySelector('.ug-oktoberfest-deals__compare');
    const badge = card.querySelector('[data-ok-badge-auto]');
    const number = badge && badge.querySelector('[data-ok-badge-percent]');
    if (!initialCards.has(card)) {
      initialCards.set(card, {
        label: label && label.textContent,
        value: value && value.textContent,
        compare: compare && compare.textContent,
        compareHidden: compare && compare.hidden,
        badgeHidden: badge && badge.hidden,
        percent: number && number.textContent
      });
    }
    if (extraPrice == null) {
      if (!card.hasAttribute('data-ok-aiod-price')) return;
      const initial = initialCards.get(card);
      if (label) label.textContent = initial.label;
      if (value) value.textContent = initial.value;
      if (compare) {
        compare.textContent = initial.compare;
        compare.hidden = initial.compareHidden;
      }
      if (badge) badge.hidden = initial.badgeHidden;
      if (number) number.textContent = initial.percent;
      card.removeAttribute('data-ok-aiod-price');
      return;
    }
    const extraLabel = (card.getAttribute('data-ok-extra-label') || '').trim() || (card.getAttribute('data-ok-price-label') || '').trim();
    const labelText = extraLabel ? extraLabel + ' ' : '';
    if (label && label.textContent !== labelText) label.textContent = labelText;
    if (card.getAttribute('data-ok-aiod-price') === String(extraPrice)) return;
    const comparePrice = card.getAttribute('data-ok-compare-price') || card.getAttribute('data-ok-regular-price') || '';
    const compareCents = Number(card.getAttribute('data-ok-compare-price-cents') ?? card.getAttribute('data-ok-regular-price-cents'));
    const priceCents = Math.round(extraPrice * 100);

    if (value) value.textContent = formatMoney(extraPrice);
    if (compare) {
      compare.textContent = comparePrice;
      compare.hidden = !comparePrice || compareCents <= priceCents;
    }
    card.setAttribute('data-ok-aiod-price', String(extraPrice));

    if (badge) {
      const percent = compareCents > priceCents && priceCents >= 0
        ? Math.floor((compareCents - priceCents) * 100 / compareCents)
        : 0;
      if (number) number.textContent = String(percent);
      badge.hidden = percent < 1;
    }
  }

  function applyPrices() {
    const root = document.querySelector(ROOT);
    if (!root) return;
    root.querySelectorAll(SECTION).forEach((section) => {
      const cards = section.querySelectorAll(CARD);
      if (!cards.length) return;
      const tables = collectAiodPrices(section);
      cards.forEach((card) => applyCard(card, tables));
    });
  }

  function start() {
    applyPrices();
    document.addEventListener('AIOD_DISCOUNT_ELIGIBLE_ITEMS_UPDATED', applyPrices);
    document.addEventListener('AIOD_TEST_SESSION_READY', applyPrices);
    document.addEventListener('shopify:section:load', applyPrices);

    // Keep late plugin loads, rule edits and scheduled expirations in sync.
    // One page-level timer remains idle while the page is hidden.
    window.setInterval(() => {
      if (document.visibilityState !== 'hidden') applyPrices();
    }, REFRESH_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') applyPrices();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
